import React from 'react';
import { Alert, Button, Switch, Divider, Form, Input, Spin, message } from 'antd';
import { SecurityScanOutlined, MoonOutlined, SunOutlined, KeyOutlined, LockOutlined, MailOutlined, CodeOutlined } from '@ant-design/icons';
import { Navigate, useNavigate } from 'react-router-dom';
import { useAuthStore, useIsAuthenticated } from '../stores/authStore';
import { useThemeStore } from '../stores/themeStore';
import { useAuthMode } from '../hooks/useAuthMode';
import { IS_DEV } from '../utils/appEnv';
import { useTranslation } from 'react-i18next';
import type { SsoProvider } from '../utils/authConfig';

interface LocalLoginFormValues {
  email: string;
  password: string;
}

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8082';

const SsoProviderButton: React.FC<{ provider: SsoProvider }> = ({ provider }) => {
  const handleClick = () => {
    // Remember which provider started the flow so the callback page calls the
    // matching /auth/sso/<provider>/callback. Otherwise it falls back to the
    // keycloak alias and the backend rejects with "Provider mismatch" (the
    // oauth_state stores the real provider, e.g. azure).
    sessionStorage.setItem('sso_provider', provider.name);
    // login_url is a full API path (/api/v1/auth/sso/<name>/login) — prepend the
    // backend origin so the browser navigates to the backend, not the frontend.
    const redirectUri = `${window.location.origin}/auth/callback`;
    const target = `${API_URL}${provider.login_url}?redirect_uri=${encodeURIComponent(redirectUri)}`;
    window.location.assign(target);
  };

  return (
    <Button
      type="primary"
      size="large"
      icon={<KeyOutlined />}
      onClick={handleClick}
      style={{ width: '100%', marginBottom: 8 }}
    >
      {provider.display_name}
    </Button>
  );
};

const LoginPage: React.FC = () => {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const isAuthenticated = useIsAuthenticated();
  const setToken = useAuthStore((state) => state.setToken);
  const setUser = useAuthStore((state) => state.setUser);
  const { mode, toggleMode } = useThemeStore();
  const { authMode, providers, loading, ok, refetch } = useAuthMode();
  const [submitting, setSubmitting] = React.useState(false);

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  const handleDevLogin = () => {
    if (!IS_DEV) return;
    setToken('local-dev-token');
    navigate('/dashboard');
  };

  const handleLocalLogin = async (values: LocalLoginFormValues) => {
    setSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/v1/auth/local/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: values.email, password: values.password }),
        // Required so the browser stores the HttpOnly refresh cookie returned
        // via Set-Cookie (fetch drops Set-Cookie without credentials:'include').
        credentials: 'include',
      });

      const data = await response.json();

      if (!response.ok) {
        const errorMsg = data?.error || t('auth.invalidCreds');
        message.error(errorMsg);
        return;
      }

      // Refresh token is now in the HttpOnly cookie; only the access token is in
      // the body. Hold it in memory.
      const { token, user } = data.data;
      setToken(token);
      if (user) {
        setUser({
          id: user.id,
          email: user.email,
          username: user.username,
          full_name: user.full_name,
        });
      }

      window.location.href = '/';
    } catch {
      message.error(t('auth.connError'));
    } finally {
      setSubmitting(false);
    }
  };

  const renderAuthContent = () => {
    if (loading) {
      return (
        <div style={{ textAlign: 'center', padding: '20px 0' }}>
          <Spin size="large" />
        </div>
      );
    }

    if (providers.length > 0) {
      return (
        <>
          {providers.map((provider) => (
            <SsoProviderButton key={provider.name} provider={provider} />
          ))}
          <Divider />
        </>
      );
    }

    // SSO deployment with no providers resolved (ok=false) — a transient state
    // during a backend rollout / Keycloak-discovery blip. DON'T flap to the local
    // login form (that strands the user on local auth in an SSO deployment until a
    // manual reload, which is exactly the prod symptom). Show a retry instead.
    if (authMode !== 'LOCAL' && !ok) {
      return (
        <div style={{ textAlign: 'center', padding: '8px 0' }}>
          <Alert
            type="warning"
            showIcon
            message={t('auth.ssoUnavailableTitle')}
            description={t('auth.ssoUnavailableDesc')}
            style={{ textAlign: 'left', marginBottom: 16 }}
          />
          <Button type="primary" block onClick={refetch} loading={loading}>
            {t('auth.retry')}
          </Button>
        </div>
      );
    }

    // Genuine LOCAL mode — show the local login form.
    return (
      <Form
        layout="vertical"
        onFinish={handleLocalLogin}
        style={{ textAlign: 'left' }}
      >
        <Form.Item
          name="email"
          rules={[
            { required: true, message: t('auth.enterEmail') },
            { type: 'email', message: t('auth.validEmail') },
          ]}
        >
          <Input
            prefix={<MailOutlined />}
            placeholder={t('auth.emailPlaceholder')}
            size="large"
            autoComplete="email"
          />
        </Form.Item>
        <Form.Item
          name="password"
          rules={[{ required: true, message: t('auth.enterPassword') }]}
        >
          <Input.Password
            prefix={<LockOutlined />}
            placeholder={t('auth.passwordPlaceholder')}
            size="large"
            autoComplete="current-password"
          />
        </Form.Item>
        <Form.Item style={{ marginBottom: 0 }}>
          <Button
            type="primary"
            htmlType="submit"
            size="large"
            loading={submitting}
            style={{ width: '100%' }}
          >
            {t('auth.login')}
          </Button>
        </Form.Item>
      </Form>
    );
  };

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'radial-gradient(ellipse at 60% 40%, rgba(99,102,241,.18) 0%, var(--bg) 60%)',
      fontFamily: 'var(--sans)',
      position: 'relative',
    }}>
      <div style={{ position: 'absolute', top: '20px', right: '20px' }}>
        <Switch
          checked={mode === 'dark'}
          onChange={toggleMode}
          checkedChildren={<MoonOutlined />}
          unCheckedChildren={<SunOutlined />}
        />
      </div>
      <div style={{
        width: 400,
        background: 'var(--surface)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-xl)',
        padding: '32px 36px',
        boxShadow: 'var(--shadow-lg)',
      }}>
        <div style={{ textAlign: 'center', marginBottom: 28 }}>
          <div style={{
            width: 44, height: 44, borderRadius: 12,
            background: 'linear-gradient(135deg, var(--brand-500), var(--brand-700))',
            display: 'grid', placeItems: 'center', color: '#fff',
            boxShadow: '0 0 20px var(--brand-glow)',
            margin: '0 auto 12px',
          }}>
            <SecurityScanOutlined style={{ fontSize: 22 }} />
          </div>
          <div style={{ fontSize: 20, fontWeight: 700, letterSpacing: '-.01em', color: 'var(--text)' }}>
            Security Hub
          </div>
          <div style={{ fontSize: 12, color: 'var(--text-3)', marginTop: 4 }}>
            {t('auth.signInToSystem')}
          </div>
        </div>

        {renderAuthContent()}

        {IS_DEV && (
          <>
            <Divider plain style={{ fontSize: 11, color: 'var(--text-3)', borderColor: 'var(--border)' }}>
              {t('auth.devOnly')}
            </Divider>
            <Button
              size="large"
              icon={<CodeOutlined />}
              onClick={handleDevLogin}
              style={{ width: '100%', opacity: 0.75 }}
            >
              {t('auth.loginDev')}
            </Button>
          </>
        )}
      </div>
    </div>
  );
};

export default LoginPage;
